<template>
  <Teleport to="body">
    <dialog
      ref="dialog"
      class="email-verification"
      aria-labelledby="email-verification-title"
      @cancel.prevent="close"
    >
      <form class="email-verification__form" @submit.prevent="verify">
        <header class="email-verification__header">
          <h3 id="email-verification-title">{{ t('nyro.verify') }}</h3>
          <button
            type="button"
            class="email-verification__close"
            :aria-label="t('common.close')"
            :disabled="busy"
            @click="close"
            >×</button
          >
        </header>
        <p v-if="usableEmail" class="email-verification__email">{{ email }}</p>
        <BaseButton
          type="button"
          monochrome
          size="lg"
          block
          variant="ghost"
          :disabled="busy"
          @click="editingEmail = true"
          >{{ t('profile.changeEmail') }}</BaseButton
        >
        <BaseButton
          v-if="usableEmail"
          type="button"
          monochrome
          size="lg"
          block
          :disabled="busy"
          data-testid="send-email-code"
          @click="sendCode"
          >{{ t('profile.getCode') }}</BaseButton
        >
        <template v-if="sent">
          <OtpCodeInput
            v-model="code"
            :label="t('profile.verificationCode')"
            :disabled="busy"
            @complete="verify"
          />
          <BaseButton
            type="submit"
            monochrome
            size="lg"
            block
            :disabled="busy || !validCode"
            >{{ t('wallet.security.confirm') }}</BaseButton
          >
        </template>
        <p v-if="message || error" role="alert">{{ message || error }}</p>
        <BaseButton
          v-if="verified && error"
          type="button"
          monochrome
          size="lg"
          block
          :disabled="busy"
          data-testid="retry-handoff"
          @click="emit('verified')"
          >{{ t('common.continue') }}</BaseButton
        >
      </form>
    </dialog>
  </Teleport>
  <EmailChangeModal
    v-if="editingEmail"
    :visible="true"
    @close="editingEmail = false"
    @saved="emailChanged"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import BaseButton from '@/shared/components/base/BaseButton.vue'
import { getErrorMessage } from '@/shared/utils/apiErrorParser'
import OtpCodeInput from '../../../../../auth-widget/src/components/OtpCodeInput.vue'
import { useProfileStore } from '../store/profileStore'
import EmailChangeModal from './EmailChangeModal.vue'

const props = defineProps<{ pending: boolean; error: string }>()
const emit = defineEmits<{ close: []; verified: [] }>()
const { t } = useI18n()
const profile = useProfileStore()
const email = computed(() => profile.profile?.email ?? '')
const usableEmail = computed(
  () => !!email.value && !/@telegram\.org$/i.test(email.value),
)
const dialog = ref<HTMLDialogElement | null>(null)
const editingEmail = ref(false)
const working = ref(false)
const busy = computed(() => working.value || props.pending)
const sent = ref(false)
const verified = ref(false)
const code = ref<string[]>(Array(6).fill(''))
const validCode = computed(() => /^[0-9]{6}$/.test(code.value.join('')))
const message = ref('')
let active = true
let generation = 0

onMounted(() => dialog.value?.showModal())
onBeforeUnmount(() => {
  active = false
})
watch(
  editingEmail,
  (editing) => {
    if (editing) dialog.value?.close()
    else dialog.value?.showModal()
  },
  { flush: 'post' },
)
watch([email, () => profile.profile?.id], () => {
  generation++
  working.value = false
  sent.value = false
  verified.value = false
  code.value = Array(6).fill('')
  message.value = ''
}, { flush: 'sync' })

function close(): void {
  if (!busy.value) {
    active = false
    emit('close')
  }
}

function emailChanged(): void {
  if (active) {
    verified.value = true
    emit('verified')
  }
}

async function sendCode(): Promise<void> {
  if (busy.value || !usableEmail.value) return
  working.value = true
  message.value = ''
  const recipient = email.value
  const accountId = profile.profile?.id
  const attempt = generation
  const isCurrent = () => active && attempt === generation && recipient === email.value && accountId === profile.profile?.id
  try {
    await profile.sendCurrentEmailVerificationCode(isCurrent)
    if (!isCurrent()) return
    sent.value = true
    code.value = Array(6).fill('')
    message.value = t('profile.codeSentTo', { email: recipient })
  } catch (cause) {
    if (isCurrent()) message.value = getErrorMessage(cause)
  } finally {
    if (isCurrent()) working.value = false
  }
}

async function verify(): Promise<void> {
  if (busy.value || !sent.value || !validCode.value) return
  working.value = true
  message.value = ''
  const recipient = email.value
  const accountId = profile.profile?.id
  const attempt = generation
  const isCurrent = () => active && attempt === generation && recipient === email.value && accountId === profile.profile?.id
  try {
    const verified = await profile.verifyCurrentEmail(code.value.join(''), isCurrent)
    if (!isCurrent()) return
    if (verified) {
      await profile.fetchProfile(true, isCurrent)
      if (isCurrent() && !profile.error) emailChanged()
      else if (isCurrent()) message.value = t('common.error')
    } else message.value = t('common.error')
  } catch (cause) {
    if (isCurrent()) message.value = getErrorMessage(cause)
  } finally {
    if (isCurrent()) {
      code.value = Array(6).fill('')
      working.value = false
    }
  }
}
</script>

<style scoped lang="scss">
.email-verification {
  width: min(440px, calc(100% - 32px));
  max-height: 90dvh;
  padding: 24px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-modal);
  background: var(--ds-surface);
  color: var(--ds-text);
  &::backdrop {
    background: var(--ds-scrim);
  }
  &__form {
    display: grid;
    gap: 16px;
  }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  &__email {
    overflow-wrap: anywhere;
  }
  &__close {
    display: grid;
    place-items: center;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--hm-text-muted);
    font: inherit;
    font-size: var(--ds-text-size-h-sm);
    cursor: pointer;
    &:focus-visible {
      outline: 2px solid var(--ds-text);
      outline-offset: 3px;
    }
    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }
}
</style>
